import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { styled } from 'styled-components';
import type { ProjectTemplate } from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import {
  AlertInline,
  AnimatedHeight,
  Box,
  List,
  ListItem,
  ListSection,
  Text,
  theme,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/TemplatesList';
import { TemplateRow, TemplateRowContents } from './TemplateRow';

TEXT.DEFINE({
  /** Aria label for the templates list */
  templatesListAriaLabel: 'Templates List',
  /** Section label for templates */
  templates: 'Templates',
  /** Message explaining why templates are only English for now */
  templateTranslationMessage:
    'Templates are currently available in English only. Additional languages will be available soon.',
});

const BLANK_TEMPLATE_HEIGHT = 120;
const MOBILE_BASE_HEIGHT = 498;
const DESKTOP_HEADER_HEIGHT = 48;
const WORKSPACE_PICKER_HEIGHT = 72;
const ANIMATION_DURATION = 250;
const TRANSLATION_MESSAGE_HEIGHT = 48 + 16;

const LINEAR = [0.2, 0.4, 0.1, 1] as const;

export type TemplateWithToggle = ProjectTemplate & {
  shouldIncludeAssets: boolean;
  setShouldIncludeAssets: (value: boolean) => void;
  fieldValidation: {
    exceedsLimit: boolean;
  };
};

export type Props = {
  selectedTemplateId?: string;
  onTemplateSelect: (templateId: string) => void;
  shouldIncludeSampleAsset: (templateId: string) => boolean;
  onToggleSampleAsset: (templateId: string, value: boolean) => void;
  templatesWithToggleState: TemplateWithToggle[];
  hasWorkspacePicker?: boolean;
  children?: ReactNode;
  isMobile?: boolean;
};

function linear(t: number) {
  return t;
}

export const TemplatesList = (props: Props) => {
  const {
    selectedTemplateId,
    onTemplateSelect,
    templatesWithToggleState = [],
    isMobile,
    children,
    hasWorkspacePicker = false,
  } = props;
  const isNotEnglishLocale = intl().locale !== 'en-US';

  const getHeaderHeight = useCallback(() => {
    const translationMessageHeight = isNotEnglishLocale
      ? TRANSLATION_MESSAGE_HEIGHT
      : 0;

    if (!isMobile)
      return (
        BLANK_TEMPLATE_HEIGHT + DESKTOP_HEADER_HEIGHT + translationMessageHeight
      );

    const pickerHeight = hasWorkspacePicker ? 0 : WORKSPACE_PICKER_HEIGHT;

    return (
      BLANK_TEMPLATE_HEIGHT +
      MOBILE_BASE_HEIGHT -
      pickerHeight +
      translationMessageHeight
    );
  }, [isMobile, isNotEnglishLocale, hasWorkspacePicker]);

  const [blankTemplate, ...otherTemplates] = templatesWithToggleState;

  return (
    <List
      animate={true}
      aria-label={TEXT.templatesListAriaLabel}
      disallowEmptySelection={true}
      getAutoScrollConfig={() => ({
        alignment: 'center',
        behavior: {
          type: 'smooth',
          ease: linear,
          duration: ANIMATION_DURATION,
          force: true,
        },
      })}
      itemGap={16}
      keyboardNavigationBehavior="tab"
      onAction={(key) => onTemplateSelect(String(key))}
      onSelectionChange={(keys) =>
        onTemplateSelect(Array.from(keys)[0] as string)
      }
      paddingY={8}
      selectedKeys={selectedTemplateId ? [selectedTemplateId] : []}
      selectionBehavior="replace"
      selectionMode="single"
      variant="unstyled"
    >
      <ListSection
        headerHeight={getHeaderHeight()}
        items={otherTemplates}
        title={
          <>
            {children && (
              <Box paddingX={24} paddingY={16}>
                {children}
              </Box>
            )}

            <BlankTemplateContainer
              $isMobile={isMobile}
              onClick={() => {
                onTemplateSelect(blankTemplate.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onTemplateSelect(blankTemplate.id);
                }
              }}
              tabIndex={0}
              {...(selectedTemplateId === blankTemplate.id
                ? {
                    'data-selected': true,
                  }
                : undefined)}
            >
              <TemplateRowContents
                isBlankProject={true}
                isSelected={selectedTemplateId === blankTemplate.id}
                template={blankTemplate}
              />
            </BlankTemplateContainer>

            <Box height={48} paddingX={24} paddingY={16}>
              <Text color="secondary" variant="secondary">
                {TEXT.templates}
              </Text>
            </Box>

            {isNotEnglishLocale && (
              <div style={{ margin: `0 ${theme.spaces[24]}` }}>
                <AlertInline>{TEXT.templateTranslationMessage}</AlertInline>
              </div>
            )}
          </>
        }
      >
        {(template) => (
          <ListItem
            key={template.id}
            as={TemplateContainer}
            marginX={24}
            textValue={template.name}
          >
            <AnimatedHeight
              variants={{
                collapsed: {
                  height: 0,
                  opacity: 0,
                  transition: {
                    duration: ANIMATION_DURATION / 1000,
                    ease: LINEAR,
                  },
                },
                open: {
                  height: 'auto',
                  opacity: 1,
                  transition: {
                    duration: ANIMATION_DURATION / 1000,
                    ease: LINEAR,
                  },
                },
              }}
            >
              <TemplateRow template={template} />
            </AnimatedHeight>
          </ListItem>
        )}
      </ListSection>
    </List>
  );
};

export const TemplateContainer = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.radii[8]};
  display: flex;
  overflow: hidden;

  &[data-hovered] {
    outline: 1.5px solid ${theme.colors.border.interactive.hover};
  }

  &[data-selected] {
    background: ${theme.colors.background.secondary};
    outline: 1.5px solid ${theme.colors.border.interactive.selected};
    outline-offset: -1.5px;
  }

  &[data-focus-visible],
  &:focus-visible {
    outline: 1.5px solid ${theme.colors.border.interactive.focused};
    outline-offset: 2px;
  }
`;

const BlankTemplateContainer = styled(TemplateContainer)<{
  $isMobile?: boolean;
}>`
  cursor: pointer;
  /** not using theme.spaces for the 16 because we need an static number
   *  for height for the virtualizer */
  margin: 16px ${theme.spaces[24]} 0;

  ${(p) => p.$isMobile && 'margin-top: 0;'}

  &:not([data-selected]):hover {
    outline: 1.5px solid ${theme.colors.border.interactive.hover};
  }

  &:focus-visible {
    outline: 1.5px solid ${theme.colors.border.interactive.focused};
    outline-offset: 2px;
  }
`;
